/**
 * UI Text Store — superadmin overrides for the bundled interface strings.
 *
 * Every visitor loads the overrides once at startup, so edited wording shows
 * for everyone. Only a superadmin ever turns edit mode on; the save and reset
 * calls are refused server-side for anyone else.
 *
 * The page registry records which locale keys the current page actually used,
 * so the editor can list everything on screen — including text that lives in
 * an attribute or a browser dialog and cannot be clicked.
 */

import { create } from 'zustand';
import { api } from '@/lib/api';

export interface UiTextMap {
  en: Record<string, string>;
  de: Record<string, string>;
}

// ── Page registry (plain module state — written during render) ───

const pageKeys = new Set<string>();

export function registerPageKey(key: string) {
  pageKeys.add(key);
}

export function getPageKeys(): string[] {
  return Array.from(pageKeys).sort();
}

export function clearPageKeys() {
  pageKeys.clear();
}

// ── Store ────────────────────────────────────────────────────────

interface UiTextState {
  overrides: UiTextMap;
  loaded: boolean;
  editMode: boolean;
  editingKey: string | null;
  saving: boolean;
  error: string | null;
  load: () => Promise<void>;
  setEditMode: (on: boolean) => void;
  openEditor: (key: string) => void;
  closeEditor: () => void;
  save: (key: string, en: string, de: string) => Promise<void>;
  reset: (key: string) => Promise<void>;
}

const EMPTY: UiTextMap = { en: {}, de: {} };

export const useUiTextStore = create<UiTextState>((set, get) => ({
  overrides: EMPTY,
  loaded: false,
  editMode: false,
  editingKey: null,
  saving: false,
  error: null,

  load: async () => {
    try {
      const map = await api.get<UiTextMap>('/ui-text');
      set({ overrides: { en: map?.en || {}, de: map?.de || {} }, loaded: true });
    } catch {
      // The bundled text is a complete fallback — a failed load is not fatal.
      set({ loaded: true });
    }
  },

  setEditMode: (on) => set({ editMode: on, editingKey: on ? get().editingKey : null, error: null }),

  openEditor: (key) => set({ editingKey: key, error: null }),

  closeEditor: () => set({ editingKey: null, error: null }),

  save: async (key, en, de) => {
    set({ saving: true, error: null });
    try {
      await api.put(`/ui-text/${encodeURIComponent(key)}`, { en, de });
      const current = get().overrides;
      set({
        overrides: {
          en: { ...current.en, [key]: en },
          de: { ...current.de, [key]: de },
        },
        saving: false,
        editingKey: null,
      });
    } catch (e: unknown) {
      set({ saving: false, error: (e as Error)?.message || null });
      throw e;
    }
  },

  reset: async (key) => {
    set({ saving: true, error: null });
    try {
      await api.delete(`/ui-text/${encodeURIComponent(key)}`);
      const nextEn = { ...get().overrides.en };
      const nextDe = { ...get().overrides.de };
      delete nextEn[key];
      delete nextDe[key];
      set({ overrides: { en: nextEn, de: nextDe }, saving: false, editingKey: null });
    } catch (e: unknown) {
      set({ saving: false, error: (e as Error)?.message || null });
      throw e;
    }
  },
}));
